/**
 * Payment Configuration Section for GlobalConfigPage
 * Allows admin to configure Stripe API keys (test/live)
 */
import React, { useCallback, useEffect, useState } from 'react'
import { CreditCard, Eye, EyeOff, CheckCircle2, XCircle, Loader2, AlertTriangle, HelpCircle } from 'lucide-react'
import { getApiBase, getToken } from '../lib/utils'

type PaymentConfig = {
  provider: string
  mode: 'test' | 'live'
  is_enabled: boolean
  test_secret_key_masked: string | null
  test_publishable_key_masked: string | null
  test_webhook_secret_masked: string | null
  live_secret_key_masked: string | null
  live_publishable_key_masked: string | null
  live_webhook_secret_masked: string | null
  has_test_keys: boolean
  has_live_keys: boolean
  last_key_rotation_at: string | null
  updated_at: string | null
}

type PaymentConfigForm = {
  mode: 'test' | 'live'
  is_enabled: boolean
  test_secret_key: string
  test_publishable_key: string
  test_webhook_secret: string
  live_secret_key: string
  live_publishable_key: string
  live_webhook_secret: string
}

const PROVIDER = 'stripe'

export default function PaymentConfigSection() {
  const [config, setConfig] = useState<PaymentConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [showSecrets, setShowSecrets] = useState(false)
  
  const [form, setForm] = useState<PaymentConfigForm>({
    mode: 'test',
    is_enabled: false,
    test_secret_key: '',
    test_publishable_key: '',
    test_webhook_secret: '',
    live_secret_key: '',
    live_publishable_key: '',
    live_webhook_secret: '',
  })

  const apiBase = getApiBase().replace(/\/$/, '')

  // Fetch current config
  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/global-config/payment/${PROVIDER}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      if (res.ok) {
        const data = await res.json()
        setConfig(data)
        setForm(f => ({
          ...f,
          mode: data.mode || 'test',
          is_enabled: data.is_enabled || false,
        }))
      }
    } catch (err) {
      console.error('Failed to fetch payment config:', err)
    } finally {
      setLoading(false)
    }
  }, [apiBase])

  useEffect(() => {
    fetchConfig()
  }, [fetchConfig])

  // Save config
  const handleSave = async () => {
    setSaving(true)
    setStatus(null)
    
    try {
      // Only send fields that have values
      const payload: Record<string, any> = {
        mode: form.mode,
        is_enabled: form.is_enabled,
      }
      
      // Add keys only if they have values (not empty)
      if (form.test_secret_key) payload.test_secret_key = form.test_secret_key
      if (form.test_publishable_key) payload.test_publishable_key = form.test_publishable_key
      if (form.test_webhook_secret) payload.test_webhook_secret = form.test_webhook_secret
      if (form.live_secret_key) payload.live_secret_key = form.live_secret_key
      if (form.live_publishable_key) payload.live_publishable_key = form.live_publishable_key
      if (form.live_webhook_secret) payload.live_webhook_secret = form.live_webhook_secret

      const res = await fetch(`${apiBase}/global-config/payment/${PROVIDER}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(payload),
      })

      if (res.ok) {
        const data = await res.json()
        setConfig(data)
        // Clear form key fields after successful save
        setForm(f => ({
          ...f,
          test_secret_key: '',
          test_publishable_key: '',
          test_webhook_secret: '',
          live_secret_key: '',
          live_publishable_key: '',
          live_webhook_secret: '',
        }))
        setStatus({ type: 'success', message: 'Configuration sauvegardée' })
      } else {
        const err = await res.json()
        setStatus({ type: 'error', message: err.detail || 'Erreur lors de la sauvegarde' })
      }
    } catch (err) {
      setStatus({ type: 'error', message: 'Erreur de connexion' })
    } finally {
      setSaving(false)
    }
  }

  // Test connection
  const handleTest = async () => {
    setTesting(true)
    setStatus(null)
    
    try {
      const res = await fetch(`${apiBase}/global-config/payment/${PROVIDER}/test`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      
      const data = await res.json()
      setStatus({
        type: data.success ? 'success' : 'error',
        message: data.message,
      })
    } catch (err) {
      setStatus({ type: 'error', message: 'Erreur lors du test' })
    } finally {
      setTesting(false)
    }
  }

  if (loading) {
    return (
      <section className="space-y-6 rounded-3xl border border-emerald-100/70 bg-white/95 p-8 shadow-lg shadow-emerald-500/10 dark:border-emerald-900/50 dark:bg-zinc-950/80">
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
        </div>
      </section>
    )
  }

  return (
    <section className="space-y-6 rounded-3xl border border-emerald-100/70 bg-white/95 p-8 shadow-lg shadow-emerald-500/10 dark:border-emerald-900/50 dark:bg-zinc-950/80">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b border-emerald-200/50 dark:border-emerald-800/50">
        <span className="text-3xl">💳</span>
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-emerald-900 dark:text-emerald-100">
            Paiements (Stripe)
          </h2>
          <p className="text-sm text-emerald-700/70 dark:text-emerald-300/70">
            Configuration des clés API Stripe pour les abonnements
          </p>
        </div>
        
        {/* Status badge */}
        {config && (
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
            config.is_enabled
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
              : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400'
          }`}>
            {config.is_enabled ? (
              <>
                <CheckCircle2 className="h-3.5 w-3.5" />
                {config.mode === 'live' ? 'Production' : 'Test'}
              </>
            ) : (
              <>
                <XCircle className="h-3.5 w-3.5" />
                Désactivé
              </>
            )}
          </div>
        )}
      </div>

      {/* Status message */}
      {status && (
        <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
          status.type === 'success'
            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
            : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300'
        }`}>
          {status.type === 'success' ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <XCircle className="h-4 w-4" />
          )}
          {status.message}
        </div>
      )}

      {/* Mode Toggle */}
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
            Mode :
          </label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, mode: 'test' }))}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                form.mode === 'test'
                  ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'
                  : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400'
              }`}
            >
              🧪 Test
            </button>
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, mode: 'live' }))}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                form.mode === 'live'
                  ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200'
                  : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400'
              }`}
            >
              🔴 Production
            </button>
          </div>
          
          <div className="flex-1" />
          
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_enabled}
              onChange={e => setForm(f => ({ ...f, is_enabled: e.target.checked }))}
              className="h-4 w-4 rounded border-zinc-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span className="text-sm text-zinc-700 dark:text-zinc-300">Activer les paiements</span>
          </label>
        </div>

        {/* Warning for live mode */}
        {form.mode === 'live' && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20">
            <AlertTriangle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
            <p className="text-sm text-red-700 dark:text-red-300">
              <strong>Attention :</strong> En mode Production, les vrais paiements seront traités.
              Assurez-vous que vos clés LIVE sont correctes avant d'activer.
            </p>
          </div>
        )}
      </div>

      {/* Keys Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">
            Clés API {form.mode === 'live' ? 'Production' : 'Test'}
          </h3>
          <button
            type="button"
            onClick={() => setShowSecrets(!showSecrets)}
            className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
          >
            {showSecrets ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {showSecrets ? 'Masquer' : 'Afficher'}
          </button>
        </div>

        <div className="grid gap-4">
          {/* Secret Key */}
          <div>
            <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-1.5">
              Clé secrète (Secret Key) *
            </label>
            <div className="relative">
              <input
                type={showSecrets ? 'text' : 'password'}
                value={form.mode === 'live' ? form.live_secret_key : form.test_secret_key}
                onChange={e => setForm(f => ({
                  ...f,
                  [form.mode === 'live' ? 'live_secret_key' : 'test_secret_key']: e.target.value
                }))}
                placeholder={
                  form.mode === 'live'
                    ? config?.live_secret_key_masked || 'sk_live_...'
                    : config?.test_secret_key_masked || 'sk_test_...'
                }
                className="w-full rounded-lg border border-zinc-200 bg-white px-4 py-2.5 text-sm font-mono dark:border-zinc-700 dark:bg-zinc-900 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
              />
            </div>
          </div>

          {/* Publishable Key */}
          <div>
            <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-1.5">
              Clé publique (Publishable Key) *
            </label>
            <input
              type="text"
              value={form.mode === 'live' ? form.live_publishable_key : form.test_publishable_key}
              onChange={e => setForm(f => ({
                ...f,
                [form.mode === 'live' ? 'live_publishable_key' : 'test_publishable_key']: e.target.value
              }))}
              placeholder={
                form.mode === 'live'
                  ? config?.live_publishable_key_masked || 'pk_live_...'
                  : config?.test_publishable_key_masked || 'pk_test_...'
              }
              className="w-full rounded-lg border border-zinc-200 bg-white px-4 py-2.5 text-sm font-mono dark:border-zinc-700 dark:bg-zinc-900 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
            />
          </div>

          {/* Webhook Secret */}
          <div>
            <label className="flex items-center gap-1.5 text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-1.5">
              Secret Webhook (optionnel)
              <div className="relative group">
                <HelpCircle className="h-3.5 w-3.5 text-zinc-400 cursor-help" />
                <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 w-72 p-3 rounded-lg bg-zinc-900 text-white text-xs shadow-xl dark:bg-zinc-800">
                  <p className="font-semibold mb-2">Events à configurer dans Stripe :</p>
                  <ul className="space-y-1 text-zinc-300">
                    <li>• checkout.session.completed</li>
                    <li>• customer.subscription.created</li>
                    <li>• customer.subscription.updated</li>
                    <li>• customer.subscription.deleted</li>
                    <li>• invoice.paid</li>
                    <li>• invoice.payment_failed</li>
                  </ul>
                  <p className="mt-2 text-zinc-400 border-t border-zinc-700 pt-2">
                    URL du webhook :<br/>
                    <code className="text-emerald-400 text-[10px] break-all">/api/billing/webhooks/stripe</code>
                  </p>
                  <div className="absolute left-3 bottom-0 translate-y-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-zinc-900 dark:border-t-zinc-800" />
                </div>
              </div>
            </label>
            <input
              type={showSecrets ? 'text' : 'password'}
              value={form.mode === 'live' ? form.live_webhook_secret : form.test_webhook_secret}
              onChange={e => setForm(f => ({
                ...f,
                [form.mode === 'live' ? 'live_webhook_secret' : 'test_webhook_secret']: e.target.value
              }))}
              placeholder={
                form.mode === 'live'
                  ? config?.live_webhook_secret_masked || 'whsec_...'
                  : config?.test_webhook_secret_masked || 'whsec_...'
              }
              className="w-full rounded-lg border border-zinc-200 bg-white px-4 py-2.5 text-sm font-mono dark:border-zinc-700 dark:bg-zinc-900 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
            />
          </div>
        </div>

        {/* Current keys status */}
        {config && (
          <div className="flex items-center gap-4 text-xs text-zinc-500 dark:text-zinc-400">
            <span className={config.has_test_keys ? 'text-emerald-600 dark:text-emerald-400' : ''}>
              {config.has_test_keys ? '✓' : '○'} Clés Test configurées
            </span>
            <span className={config.has_live_keys ? 'text-emerald-600 dark:text-emerald-400' : ''}>
              {config.has_live_keys ? '✓' : '○'} Clés Live configurées
            </span>
            {config.last_key_rotation_at && (
              <span>
                Dernière modification : {new Date(config.last_key_rotation_at).toLocaleDateString('fr-FR')}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 pt-4 border-t border-emerald-200/50 dark:border-emerald-800/50">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <CreditCard className="h-4 w-4" />
          )}
          Sauvegarder
        </button>
        
        <button
          type="button"
          onClick={handleTest}
          disabled={testing || !config?.is_enabled}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-emerald-300 text-emerald-700 text-sm font-medium hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-emerald-700 dark:text-emerald-300 dark:hover:bg-emerald-900/20 transition-colors"
        >
          {testing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <CheckCircle2 className="h-4 w-4" />
          )}
          Tester la connexion
        </button>
      </div>

      {/* Info */}
      <div className="flex items-start gap-3 text-xs text-emerald-700 dark:text-emerald-300 pt-4 border-t border-emerald-200/50 dark:border-emerald-800/50">
        <span className="text-base">ℹ️</span>
        <p>
          <strong>Configuration Stripe :</strong> Obtenez vos clés API depuis le{' '}
          <a
            href="https://dashboard.stripe.com/apikeys"
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-600 hover:underline dark:text-emerald-400 font-medium"
          >
            Dashboard Stripe
          </a>
          . Utilisez les clés Test pour le développement et les clés Live uniquement en production.
          Le webhook secret est nécessaire pour recevoir les événements (paiements, annulations).
        </p>
      </div>
    </section>
  )
}
